import React, { useState } from 'react';
import { X, Edit3, Trash2, Save, AlertCircle } from 'lucide-react';
import { StcTransDto, stcTransService } from '../../../../services/stcTransService';

interface StcTransEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  stcTrans: StcTransDto;
  onSuccess?: () => void;
}

export const StcTransEditModal: React.FC<StcTransEditModalProps> = ({
  isOpen,
  onClose,
  stcTrans,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editedQuantity, setEditedQuantity] = useState(stcTrans.quantity || 0);
  const [editedBatchNo, setEditedBatchNo] = useState(stcTrans.itemBatchNo || '');
  const [editedBatchExpDate, setEditedBatchExpDate] = useState(
    stcTrans.itemBatchExpDate ? new Date(stcTrans.itemBatchExpDate).toISOString().split('T')[0] : ''
  );
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const handleUpdateQuantity = async () => {
    if (editedQuantity <= 0) {
      setError('Antall må være større enn 0');
      return;
    }

    // Validate batch fields based on itemType
    // ItemType=1/5: BatchNo required. ItemType=4: BatchNo optional (auto-generated from expiry date)
    if ((stcTrans.itemType === 1 || stcTrans.itemType === 5) && !editedBatchNo.trim()) {
      setError('Batchnummer er påkrevd for denne varetypen');
      return;
    }

    // ItemType=4: ExpiryDate required
    if (stcTrans.itemType === 4 && !editedBatchExpDate) {
      setError('Utløpsdato er påkrevd for denne varetypen');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prøv å bruke den nye updateTransaction metoden først
      try {
        // Konverter til CreateStcTransRequest format
        const updateRequest = {
          batchID: stcTrans.batchID,
          lineType: stcTrans.lineType,
          orderNo: stcTrans.orderNo,
          lineNo: stcTrans.lineNo,
          locationName: stcTrans.locationName,
          quantity: editedQuantity,
          transDt: stcTrans.transDt,
          warehouse: stcTrans.warehouse,
          toWarehouse: stcTrans.toWarehouse,
          operatorID: stcTrans.operatorID,
          profileID: stcTrans.profileID,
          exportedYesNo: stcTrans.exportedYesNo,
          lineStatus: stcTrans.lineStatus,
          itemNo: stcTrans.itemNo,
          itemUnitCode: stcTrans.itemUnitCode,
          itemBarCode: stcTrans.itemBarCode,
          description1: stcTrans.description1,
          itemBatchNo: editedBatchNo.trim() || undefined,
          itemBatchExpDate: editedBatchExpDate ? new Date(editedBatchExpDate) : undefined,
          itemSerialNo: stcTrans.itemSerialNo,
          erpLinkName: stcTrans.erpLinkName,
          shipmentNo: stcTrans.shipmentNo,
          quantityOrigin: stcTrans.quantityOrigin,
          itemUnitTxt: stcTrans.itemUnitTxt,
          lineTypeOrigin: stcTrans.lineTypeOrigin,
          price: stcTrans.price,
          currency: stcTrans.currency,
          reasonCode: stcTrans.reasonCode,
          transType: stcTrans.transType,
          sscc: stcTrans.sscc,
          quantityCorrected: stcTrans.quantityCorrected,
          freeField1: stcTrans.freeField1,
          freeField2: stcTrans.freeField2,
          freeField3: stcTrans.freeField3,
          appFreeField1: stcTrans.appFreeField1,
          appFreeField2: stcTrans.appFreeField2,
          quantityGross: stcTrans.quantityGross,
          transitPackagingID: stcTrans.transitPackagingID,
          clientID: stcTrans.clientID,
          stcBoxNo: stcTrans.stcBoxNo,
          grShipmentNo: stcTrans.grShipmentNo,
          externalSystemID: stcTrans.externalSystemID,
          externalSystemStatus: stcTrans.externalSystemStatus,
          itemType: stcTrans.itemType,
          orderNoERP: stcTrans.orderNoERP,
          lineNoERP: stcTrans.lineNoERP
        };

        await stcTransService.updateTransaction(stcTrans.batchID, stcTrans.lineID, updateRequest);
        
        console.log('Transaction updated successfully using PUT method');
        
      } catch (updateError: any) {
        // Hvis PUT ikke er støttet, fall tilbake til delete/create
        if (updateError.message === 'UPDATE_NOT_SUPPORTED') {
          console.log('PUT not supported, falling back to delete/create method');
          
          // Delete old transaction and create new one with updated info
          await stcTransService.deleteTransaction(stcTrans.batchID, stcTrans.lineID);
          
          const createRequest = {
            batchID: stcTrans.batchID,
            lineType: stcTrans.lineType,
            orderNo: stcTrans.orderNo,
            lineNo: stcTrans.lineNo,
            locationName: stcTrans.locationName,
            quantity: editedQuantity,
            transDt: stcTrans.transDt,
            warehouse: stcTrans.warehouse,
            toWarehouse: stcTrans.toWarehouse,
            operatorID: stcTrans.operatorID,
            profileID: stcTrans.profileID,
            exportedYesNo: stcTrans.exportedYesNo,
            lineStatus: stcTrans.lineStatus,
            itemNo: stcTrans.itemNo,
            itemUnitCode: stcTrans.itemUnitCode,
            itemBarCode: stcTrans.itemBarCode,
            description1: stcTrans.description1,
            itemBatchNo: editedBatchNo.trim() || undefined,
            itemBatchExpDate: editedBatchExpDate ? new Date(editedBatchExpDate) : undefined,
            itemSerialNo: stcTrans.itemSerialNo,
            erpLinkName: stcTrans.erpLinkName,
            shipmentNo: stcTrans.shipmentNo,
            quantityOrigin: stcTrans.quantityOrigin,
            itemUnitTxt: stcTrans.itemUnitTxt,
            lineTypeOrigin: stcTrans.lineTypeOrigin,
            price: stcTrans.price,
            currency: stcTrans.currency,
            reasonCode: stcTrans.reasonCode,
            transType: stcTrans.transType,
            sscc: stcTrans.sscc,
            quantityCorrected: stcTrans.quantityCorrected,
            freeField1: stcTrans.freeField1,
            freeField2: stcTrans.freeField2,
            freeField3: stcTrans.freeField3,
            appFreeField1: stcTrans.appFreeField1,
            appFreeField2: stcTrans.appFreeField2,
            quantityGross: stcTrans.quantityGross,
            transitPackagingID: stcTrans.transitPackagingID,
            clientID: stcTrans.clientID,
            stcBoxNo: stcTrans.stcBoxNo,
            grShipmentNo: stcTrans.grShipmentNo,
            externalSystemID: stcTrans.externalSystemID,
            externalSystemStatus: stcTrans.externalSystemStatus,
            itemType: stcTrans.itemType,
            orderNoERP: stcTrans.orderNoERP,
            lineNoERP: stcTrans.lineNoERP
          };
          
          await stcTransService.createTransaction(stcTrans.batchID, createRequest);
          
          console.log('Transaction updated using fallback delete/create method');
        } else {
          throw updateError;
        }
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(`Feil ved oppdatering: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await stcTransService.deleteTransaction(stcTrans.batchID, stcTrans.lineID);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError(`Feil ved sletting: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDeleteConfirm(false);
    setError(null);
    setEditedQuantity(stcTrans.quantity || 0);
    setEditedBatchNo(stcTrans.itemBatchNo || '');
    setEditedBatchExpDate(
      stcTrans.itemBatchExpDate ? new Date(stcTrans.itemBatchExpDate).toISOString().split('T')[0] : ''
    );
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-2xl sm:h-auto flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Edit3 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Rediger StcTrans</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                Varenummer: {stcTrans.itemNo} • Linje: {stcTrans.lineNo}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
          {/* Transaction Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Transaksjonsinformasjon</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Varenummer:</span>
                <div className="font-medium">{stcTrans.itemNo}</div>
              </div>
              <div>
                <span className="text-gray-600">Beskrivelse:</span>
                <div className="font-medium">{stcTrans.description1 || 'Ikke angitt'}</div>
              </div>
              <div>
                <span className="text-gray-600">Nåværende antall:</span>
                <div className="font-medium">{stcTrans.quantity} {stcTrans.itemUnitTxt}</div>
              </div>
              <div>
                <span className="text-gray-600">Lokasjon:</span>
                <div className="font-medium">{stcTrans.locationName || 'Ikke angitt'}</div>
              </div>
              <div>
                <span className="text-gray-600">Transdato:</span>
                <div className="font-medium">
                  {stcTrans.transDt ? new Date(stcTrans.transDt).toLocaleDateString('nb-NO') : 'Ikke angitt'}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <div className="font-medium">{stcTrans.statusText || stcTrans.lineStatus}</div>
              </div>
              <div>
                <span className="text-gray-600">Varetype:</span>
                <div className="font-medium">
                  {stcTrans.itemType || 'Ikke angitt'}
                  {stcTrans.itemType === 1 && <span className="text-sm text-blue-600 ml-2">(Batch påkrevd)</span>}
                  {stcTrans.itemType === 4 && <span className="text-sm text-blue-600 ml-2">(Utløpsdato påkrevd, batch valgfri)</span>}
                  {stcTrans.itemType === 5 && <span className="text-sm text-blue-600 ml-2">(Batch påkrevd, lagerstyrt)</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Quantity and Batch Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nytt antall
              </label>
              <input
                type="number"
                value={editedQuantity}
                onChange={(e) => setEditedQuantity(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
                step="0.1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enhet: {stcTrans.itemUnitTxt}
              </p>
            </div>
            
            {/* Batch Number - Required for itemType 1/5, optional for 4 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batchnummer {(stcTrans.itemType === 1 || stcTrans.itemType === 5) && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={editedBatchNo}
                onChange={(e) => setEditedBatchNo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  (stcTrans.itemType === 1 || stcTrans.itemType === 5) && !editedBatchNo
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder={stcTrans.itemType === 4 ? "Auto-genereres fra utløpsdato" : "Skriv inn batchnummer"}
                required={stcTrans.itemType === 1 || stcTrans.itemType === 5}
              />
            </div>

            {/* Expiry Date - Required for itemType 4, optional for itemType 5 */}
            {(stcTrans.itemType === 4 || stcTrans.itemType === 5) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Utløpsdato {stcTrans.itemType === 4 && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="date"
                  value={editedBatchExpDate}
                  onChange={(e) => setEditedBatchExpDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    stcTrans.itemType === 4 && !editedBatchExpDate
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  required={stcTrans.itemType === 4}
                />
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deleteConfirm && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <span className="text-yellow-700 font-medium">
                  Er du sikker på at du vil slette denne transaksjonen?
                </span>
              </div>
              <p className="text-sm text-yellow-600 mt-2">
                Denne handlingen kan ikke angres.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Avbryt
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDelete}
              disabled={loading}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                deleteConfirm
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              <span>{deleteConfirm ? 'Bekreft sletting' : 'Slett'}</span>
            </button>
            <button
              onClick={handleUpdateQuantity}
              disabled={loading || editedQuantity <= 0}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  <span>Lagrer...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Lagre endringer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
